import { useEffect, useState, useCallback, Fragment } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api } from "../lib/api";

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [actorRole, setActorRole] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), page_size: "30" });
      if (actorRole) p.set("actor_role", actorRole);
      if (action) p.set("action", action);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const { data } = await api.get(`/admin/audit-logs?${p.toString()}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [actorRole, action, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Audit Logs" subtitle={`${total} entries`}>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={actorRole || "all"} onValueChange={(v) => { setActorRole(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="audit-actor-role" className="w-44 rounded-full"><SelectValue placeholder="Actor role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actors</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="doctor">Doctor</SelectItem>
            <SelectItem value="clinic">Clinic</SelectItem>
            <SelectItem value="patient">Patient</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="audit-action-filter" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} placeholder="action (e.g. doctor.create)" className="w-56 rounded-full" />
        <Input data-testid="audit-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-44 rounded-full" />
        <Input data-testid="audit-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-44 rounded-full" />
      </div>

      <div data-testid="admin-audit-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="w-8"></th><th className="text-left p-3">At</th><th className="text-left p-3">Role</th>
                <th className="text-left p-3">Action</th><th className="text-left p-3">Target</th><th className="text-left p-3">Target ID</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No log entries</td></tr>
                : items.map((a, idx) => {
                  const key = a.id || `${a.at}-${idx}`;
                  const open = !!expanded[key];
                  return (
                    <Fragment key={key}>
                      <tr data-testid={`audit-row-${key}`} className="border-t border-border hover:bg-secondary/30 cursor-pointer" onClick={() => setExpanded((m) => ({ ...m, [key]: !open }))}>
                        <td className="p-3 w-8">{open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</td>
                        <td className="p-3 text-xs">{new Date(a.at).toLocaleString("en-IN")}</td>
                        <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{a.actor_role}</span></td>
                        <td className="p-3 font-mono text-xs">{a.action}</td>
                        <td className="p-3 text-xs">{a.target_type}</td>
                        <td className="p-3 font-mono text-xs">{a.target_id}</td>
                      </tr>
                      {open && (
                        <tr className="bg-secondary/20">
                          <td colSpan={6} className="p-3">
                            <pre data-testid={`audit-payload-${key}`} className="text-xs bg-white border border-border rounded-xl p-3 overflow-x-auto font-mono whitespace-pre-wrap">{JSON.stringify(a.payload || {}, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {total > 30 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 30)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
